import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Ancho (px CSS) al que se maqueta el clon que se rasteriza. Fija la relación
// texto/página: 900px repartidos sobre los 180mm de ancho útil dejan el cuerpo
// del reporte en ~9pt, independientemente del tamaño de la ventana.
const CONTENT_WIDTH_PX = 900;

const MARGIN_X_MM = 15;
const MARGIN_Y_MM = 20;

// Si respetar un bloque indivisible obliga a cerrar la página con menos de
// este porcentaje ocupado, conviene cortar igual antes que dejar media página
// en blanco (caso típico: un gráfico más alto que la página útil).
const MIN_PAGE_FILL = 0.35;

// Acota las secuencias de líneas en blanco a dos saltos, para que el espacio
// entre párrafos sea siempre el mismo.
export function collapseBlankLines(text) {
    return String(text ?? '')
        .replace(/\r\n/g, '\n')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

// Devuelve, en px CSS relativos a `root`, los rangos verticales que no se
// pueden partir sin cortar una línea de texto u otro elemento visual
// (gráficos, imágenes). Sirven para elegir un salto de página que caiga entre
// líneas y nunca en medio de una.
export function collectUnbreakableRanges(root) {
    const ranges = [];
    const rootTop = root.getBoundingClientRect().top;

    const addRect = (rect) => {
        const top = rect.top - rootTop;
        const bottom = rect.bottom - rootTop;
        if (bottom > top) ranges.push([top, bottom]);
    };

    // Range.getClientRects() devuelve un rectángulo por cada línea visual en
    // la que envuelve un nodo de texto, no uno por párrafo.
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => (node.nodeValue && node.nodeValue.trim()
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT),
    });
    let textNode = walker.nextNode();
    while (textNode) {
        const range = document.createRange();
        range.selectNodeContents(textNode);
        Array.from(range.getClientRects()).forEach(addRect);
        textNode = walker.nextNode();
    }

    root.querySelectorAll('canvas, svg, img').forEach((el) => addRect(el.getBoundingClientRect()));

    ranges.sort((a, b) => a[0] - b[0]);
    return ranges;
}

// Retrocede el corte ideal hasta que deje de caer dentro de un bloque
// indivisible. Si para lograrlo hay que subir más allá de `minY` (el corte
// dejaría la página vacía), se corta en el punto ideal.
export function findLineSafeBreak(ranges, idealY, minY) {
    let y = idealY;
    let moved = true;
    while (moved) {
        moved = false;
        for (const [top, bottom] of ranges) {
            if (y > top && y < bottom) {
                y = top;
                moved = true;
            }
        }
        if (y <= minY) return idealY;
    }
    return y;
}

// Clona el reporte fuera de pantalla y le aplica los ajustes que valen sólo
// para el PDF (ancho fijo, sin recuadro, párrafos normalizados). La vista del
// usuario queda intacta.
function buildPdfClone(element) {
    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = [
        'position:absolute',
        'left:-10000px',
        'top:0',
        'z-index:-1',
        'pointer-events:none',
        'background:#ffffff',
        `width:${CONTENT_WIDTH_PX}px`,
    ].join(';');

    const clone = element.cloneNode(true);
    clone.style.width = `${CONTENT_WIDTH_PX}px`;
    clone.style.maxWidth = 'none';
    clone.style.margin = '0';
    clone.style.padding = '0';

    clone.querySelectorAll('[data-pdf-unframe]').forEach((el) => {
        el.style.border = 'none';
        el.style.padding = '0';
    });

    clone.querySelectorAll('[data-pdf-normalize-text]').forEach((el) => {
        el.textContent = collapseBlankLines(el.textContent);
    });

    // cloneNode no copia el bitmap de un <canvas>; hay que repintarlo.
    const sourceCanvases = element.querySelectorAll('canvas');
    const clonedCanvases = clone.querySelectorAll('canvas');
    clonedCanvases.forEach((target, index) => {
        const source = sourceCanvases[index];
        if (!source || !source.width || !source.height) return;
        try {
            target.getContext('2d').drawImage(source, 0, 0);
        } catch {
            // Un canvas "tainted" no se puede copiar; se rasteriza vacío.
        }
    });

    // Los ids duplicados (defs de los SVG) harían que el clon resuelva sus
    // url(#...) contra el original; se les da un espacio de nombres propio.
    const renamedIds = new Map();
    clone.querySelectorAll('[id]').forEach((el) => {
        renamedIds.set(el.id, `${el.id}-pdf`);
        el.id = `${el.id}-pdf`;
    });
    if (renamedIds.size) {
        clone.querySelectorAll('*').forEach((node) => {
            Array.from(node.attributes).forEach((attr) => {
                if (!attr.value.includes('url(#')) return;
                const next = attr.value.replace(
                    /url\(#([^)]+)\)/g,
                    (match, id) => (renamedIds.has(id) ? `url(#${renamedIds.get(id)})` : match),
                );
                if (next !== attr.value) node.setAttribute(attr.name, next);
            });
        });
    }

    host.appendChild(clone);
    document.body.appendChild(host);
    return { host, clone };
}

export async function generateReportPdf(element, fileName) {
    const { host, clone } = buildPdfClone(element);

    try {
        const unbreakableRanges = collectUnbreakableRanges(clone);
        const cloneWidthPx = clone.getBoundingClientRect().width || CONTENT_WIDTH_PX;

        const canvas = await html2canvas(clone, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
        });
        const canvasPxPerCssPx = canvas.width / cloneWidthPx;

        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const contentWidthMm = pageWidth - MARGIN_X_MM * 2;
        const contentHeightMm = pageHeight - MARGIN_Y_MM * 2;

        const pxPerMm = canvas.width / contentWidthMm;
        const pageContentHeightPx = contentHeightMm * pxPerMm;

        let sourceY = 0;
        let isFirstPage = true;

        while (sourceY < canvas.height - 1) {
            if (!isFirstPage) pdf.addPage();
            isFirstPage = false;

            const maxSliceHeightPx = Math.min(pageContentHeightPx, canvas.height - sourceY);
            const isLastSlice = sourceY + maxSliceHeightPx >= canvas.height - 1;
            let sliceHeightPx = maxSliceHeightPx;

            if (!isLastSlice) {
                const idealBreakCssY = (sourceY + maxSliceHeightPx) / canvasPxPerCssPx;
                const minCssY = sourceY / canvasPxPerCssPx;
                const safeBreakCssY = findLineSafeBreak(unbreakableRanges, idealBreakCssY, minCssY);
                const safeBreakCanvasY = safeBreakCssY * canvasPxPerCssPx;
                if (safeBreakCanvasY - sourceY > maxSliceHeightPx * MIN_PAGE_FILL) {
                    sliceHeightPx = safeBreakCanvasY - sourceY;
                }
            }

            sliceHeightPx = Math.max(1, Math.round(sliceHeightPx));

            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = canvas.width;
            sliceCanvas.height = sliceHeightPx;
            const sliceCtx = sliceCanvas.getContext('2d');
            sliceCtx.fillStyle = '#ffffff';
            sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
            sliceCtx.drawImage(
                canvas,
                0, sourceY, canvas.width, sliceHeightPx,
                0, 0, canvas.width, sliceHeightPx,
            );

            pdf.addImage(
                sliceCanvas.toDataURL('image/png'),
                'PNG',
                MARGIN_X_MM,
                MARGIN_Y_MM,
                contentWidthMm,
                sliceHeightPx / pxPerMm,
            );

            sourceY += sliceHeightPx;
        }

        pdf.save(fileName);
    } finally {
        host.remove();
    }
}

export function buildReportPdfName(nombre, id) {
    return nombre
        ? `reporte_${nombre.replace(/\s+/g, '_')}.pdf`
        : `reporte_${id}.pdf`;
}
